import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin } from 'antd';
import { useLazyFileManagerDownloadQuery } from '../../api/tenders';
import logo from '../../assets/Logo.png';
import downloadIllustration from '../../assets/Documents, File, Download.svg';
import doneCheck from '../../assets/Done, Check.svg';
import './index.scss';

function filenameFromContentDisposition(header: string | null): string | undefined {
    if (!header) return undefined;
    const utf8 = /filename\*=UTF-8''([^;\n]+)/i.exec(header);
    if (utf8?.[1]) {
        try {
            return decodeURIComponent(utf8[1].trim());
        } catch {
            return utf8[1].trim();
        }
    }
    const quoted = /filename="([^"]+)"/i.exec(header);
    if (quoted?.[1]) return quoted[1].trim();
    const plain = /filename=([^;\s]+)/i.exec(header);
    return plain?.[1]?.replace(/["']/g, '')?.trim();
}

function downloadBlob(blob: Blob, filename: string) {
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
}

export const DownloadDocument = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const signature = searchParams.get('signature');
    const [downloadBySignature] = useLazyFileManagerDownloadQuery();
    const [isDownloaded, setIsDownloaded] = useState(false);

    useEffect(() => {
        if (!signature) {
            navigate('/expired', { replace: true });
            return;
        }

        setIsDownloaded(false);
        let cancelled = false;

        downloadBySignature({ signature }).then((result) => {
            if (cancelled) return;

            if (result.error) {
                navigate('/not-found', { replace: true });
                return;
            }

            const blob = result.data;
            if (blob instanceof Blob) {
                let headerName: string | undefined;
                if ('meta' in result && result.meta && typeof result.meta === 'object' && 'response' in result.meta) {
                    const res = (result.meta as { response: Response }).response;
                    headerName = filenameFromContentDisposition(res.headers.get('Content-Disposition'));
                }
                const nameFromType = blob.type?.includes('pdf') ? 'document.pdf' : 'download';
                downloadBlob(blob, headerName || nameFromType);
                if (!cancelled) {
                    setIsDownloaded(true);
                }
            } else {
                navigate('/not-found', { replace: true });
            }
        });

        return () => {
            cancelled = true;
        };
    }, [signature, navigate, downloadBySignature]);

    return (
        <div className="download-document">
            <header className="download-document__header">
                <img src={logo} alt="" className="download-document__logo" />
            </header>
            <main className="download-document__main">
                <div className="download-document__card">
                    {isDownloaded ? (
                        <>
                            <img src={doneCheck} alt="" className="download-document__illustration" />
                            <h1 className="download-document__title">File downloaded</h1>
                            <p className="download-document__lead download-document__lead--success">
                                Your file has been saved. If nothing appeared, check your browser&apos;s downloads
                                folder.
                            </p>
                        </>
                    ) : (
                        <>
                            <img
                                src={downloadIllustration}
                                alt=""
                                className="download-document__illustration"
                            />
                            <h1 className="download-document__title">Preparing your download</h1>
                            <p className="download-document__lead">
                                Your file is being retrieved. It will start downloading in your browser in a moment —
                                please keep this page open.
                            </p>
                            <div className="download-document__spin-wrap">
                                <Spin size="large" />
                            </div>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};
